import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { List, Drawer, ListHeading, ListSubheading } from '@oliasoft-open-source/react-ui-library';
import { convertToKebabCase } from 'client/common/utils/string-utils';
import translations from 'src/internationalisation/translation-map.json';
import {
  addInitialCampaign,
  campaignSelected,
  getCampaignDetails,
  getCampaignResults,
} from 'store/entities/campaigns/campaigns';
import { useParams } from 'react-router';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import {
  selectIsGeneratingCampaignEstimates,
  selectIsRecalculatingCampaigns,
} from 'src/store/entities/campaigns/selectors';
import { CampaignStatus, CampaignTabIndex } from 'src/enums/campaigns';
import { FeatureFlags } from '@oliasoft/feature-flags';
import { FeatureFlagName } from 'src/enums/feature-flags';

const CampaignsList = ({
  campaigns,
  addInitialCampaign,
  campaignSelected,
  campaignModalOpened,
  onClickDelete,
  setActiveTabIndex,
  activeTabIndex,
  getCampaignDetails,
  getCampaignResults,
  isRecalculating,
  isGeneratingEstimates,
}) => {
  const { t } = useTranslation();
  const { company } = useParams();
  const isCampaignTimeTrackerActive = FeatureFlags.isActive(FeatureFlagName.CampaignTimeTracker);

  const handleOnClickCampaign = (campaign) => {
    const { campaignTotal, designs, estimates, campaignId, detailsFetched, isFetchingDetails } = campaign || {};
    const nextTabIndex =
      activeTabIndex === CampaignTabIndex.TimeTracker && campaign.status !== CampaignStatus.Actual
        ? CampaignTabIndex.Input
        : activeTabIndex;

    if (activeTabIndex === CampaignTabIndex.TimeTracker && campaign.status !== CampaignStatus.Actual) {
      setActiveTabIndex(nextTabIndex);
    }
    campaignSelected(campaignId);
    if (!detailsFetched && !isFetchingDetails) {
      if (nextTabIndex === CampaignTabIndex.Input) {
        getCampaignDetails(campaignId);
      } else {
        getCampaignResults(campaignId);
      }
      return;
    }
    if (campaignTotal === undefined && (!designs || designs.length === 0) && estimates?.length > 0) {
      getCampaignResults(campaignId);
    }
  };

  const buildItems = (list) =>
    list.map((campaign) => ({
      id: campaign.campaignId,
      name: campaign.name,
      active: campaign.active,
      disabled: isRecalculating || isGeneratingEstimates,
      onClick: () => handleOnClickCampaign(campaign),
      testId: `campaigns-list-${convertToKebabCase(campaign.name)}-item`,
      actions: [
        {
          label: t(translations.more),
          testId: `campaigns-list-${convertToKebabCase(campaign.name)}-more-button`,
          subActions: [
            {
              label: t(translations.edit),
              icon: 'edit',
              onClick: () => campaignModalOpened(campaign.campaignId),
              testId: `campaigns-list-${convertToKebabCase(campaign.name)}-edit-button`,
            },
            {
              label: t(translations.delete),
              icon: 'delete',
              onClick: () => onClickDelete(campaign.campaignId),
              disabled: isRecalculating || isGeneratingEstimates,
              testId: `campaigns-list-${convertToKebabCase(campaign.name)}-delete-button`,
            },
          ],
        },
      ],
    }));

  const actualCampaigns = campaigns.filter((campaign) => campaign.status === CampaignStatus.Actual);
  const prototypeCampaigns = campaigns.filter((campaign) => campaign.status !== CampaignStatus.Actual);

  const actualItems = buildItems(actualCampaigns);
  const prototypeItems = buildItems(prototypeCampaigns);

  const renderCampaignListByStatus = (status, items) => {
    const translationKey = status === CampaignStatus.Actual ? 'actual' : 'prototype';
    const testId = `campaigns-list${status === CampaignStatus.Actual ? '-actual' : ''}`;

    return (
      <>
        <ListSubheading
          item={{
            name: `${t(translations[translationKey])} (${items.length})`,
          }}
        />
        <List testId={testId} noHeader list={{ items }} />
      </>
    );
  };

  return (
    <Drawer border button closedWidth="100px" open width="320px">
      <div data-testid="campaigns-title">
        <ListHeading
          testId="campaigns-list-heading"
          name={t(translations.campaigns)}
          actions={[
            isCampaignTimeTrackerActive
              ? {
                  label: t(translations.add),
                  primary: true,
                  icon: 'add',
                  testId: 'campaigns-list-subheading-add-button',
                  subActions: [
                    {
                      label: t(translations.prototype),
                      testId: 'campaigns-list-add-prototype-button',
                      onClick: () => {
                        setActiveTabIndex(0);
                        addInitialCampaign(company, t(translations.campaigns_newCampaign), CampaignStatus.Prototype);
                      },
                    },
                    {
                      label: t(translations.actual),
                      testId: 'campaigns-list-add-actual-button',
                      onClick: () => {
                        setActiveTabIndex(0);
                        addInitialCampaign(company, t(translations.campaigns_newCampaign), CampaignStatus.Actual);
                      },
                    },
                  ],
                }
              : {
                  label: t(translations.add),
                  primary: true,
                  icon: 'add',
                  testId: 'campaigns-list-subheading-add-button',
                  onClick: () => {
                    setActiveTabIndex(0);
                    addInitialCampaign(company, t(translations.campaigns_newCampaign), CampaignStatus.Prototype);
                  },
                },
          ]}
        />
      </div>
      {isCampaignTimeTrackerActive ? (
        <>
          {renderCampaignListByStatus(CampaignStatus.Actual, actualItems)}
          {renderCampaignListByStatus(CampaignStatus.Prototype, prototypeItems)}
        </>
      ) : (
        <List testId="campaigns-list" noHeader list={{ items: prototypeItems }} />
      )}
    </Drawer>
  );
};

const mapStateToProps = ({ entities }) => ({
  isRecalculating: selectIsRecalculatingCampaigns(entities),
  isGeneratingEstimates: selectIsGeneratingCampaignEstimates(entities),
});

const mapDispatchToProps = {
  addInitialCampaign,
  campaignSelected,
  getCampaignDetails,
  getCampaignResults,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(CampaignsList), {
  style: { width: '400px' },
});

export { Container as CampaignsList };
